import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Gift, Award, Sparkles, CheckCircle, Trophy, History, TrendingUp, TrendingDown, Calendar, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { deductPoints, MILESTONES } from '@/components/loyalty/PointsHelper';
import PointsDisplay from '@/components/loyalty/PointsDisplay';

export default function Rewards() {
  const [user, setUser] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: userPoints } = useQuery({
    queryKey: ['user-points', user?.id],
    queryFn: async () => {
      const result = await base44.entities.UserPoints.filter({ user_id: user?.id });
      return result[0] || null;
    },
    enabled: !!user?.id,
  });

  const { data: rewards = [] } = useQuery({
    queryKey: ['rewards'],
    queryFn: () => base44.entities.Reward.filter({ available: true }),
  });

  const { data: userRewards = [] } = useQuery({
    queryKey: ['user-rewards', user?.id],
    queryFn: () => base44.entities.UserReward.filter({ user_id: user?.id }),
    enabled: !!user?.id,
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ['point-transactions', user?.id],
    queryFn: () => base44.entities.PointTransaction.filter({ user_id: user?.id }, '-created_date'),
    enabled: !!user?.id,
  });

  const redeemRewardMutation = useMutation({
    mutationFn: async (reward) => {
      const result = await deductPoints(user.id, reward.points_cost, 'redeem', `Canjeado: ${reward.name}`);
      
      if (!result.success) {
        throw new Error(result.error || 'Error al canjear');
      }

      return await base44.entities.UserReward.create({
        user_id: user.id,
        reward_id: reward.id,
        redeemed_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-points'] });
      queryClient.invalidateQueries({ queryKey: ['user-rewards'] });
      queryClient.invalidateQueries({ queryKey: ['point-transactions'] });
      toast.success('¡Recompensa canjeada exitosamente!');
    },
    onError: (error) => {
      toast.error(error.message || 'No tienes suficientes puntos');
    },
  });

  const hasRedeemed = (rewardId) => {
    return userRewards.some(ur => ur.reward_id === rewardId);
  };

  const canAfford = (cost) => {
    return (userPoints?.available_points || 0) >= cost;
  };

  const getActionIcon = (action) => {
    const icons = {
      scan: '🔍',
      review: '⭐',
      post: '✍️',
      comment: '💬',
      share: '🔗',
      group_join: '👥',
      invite: '📧',
      redeem: '🎁',
      milestone: '🏆'
    };
    return icons[action] || '✨';
  };

  const getActionLabel = (action) => {
    const labels = {
      scan: 'Escaneo',
      review: 'Reseña',
      post: 'Post',
      comment: 'Comentario',
      share: 'Compartir',
      group_join: 'Unión a grupo',
      invite: 'Invitación',
      redeem: 'Canje',
      milestone: 'Hito alcanzado'
    };
    return labels[action] || action;
  };

  const completedMilestones = userPoints?.milestones_completed || [];
  const availableMilestones = MILESTONES.filter(m => !completedMilestones.includes(m.id));
  const achievedMilestones = MILESTONES.filter(m => completedMilestones.includes(m.id));

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-gray-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-lg">Recompensas</h1>
              <p className="text-xs text-gray-400">Canjea tus puntos</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs defaultValue="rewards" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="rewards">Recompensas</TabsTrigger>
            <TabsTrigger value="milestones">Hitos</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="rewards" className="space-y-6">
            {userPoints && <PointsDisplay userPoints={userPoints} />}

            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-gray-900">¿Cómo ganar puntos?</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-2xl">🔍</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Escanear</p>
                    <p className="text-xs text-gray-500">+10 pts</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reseñar</p>
                    <p className="text-xs text-gray-500">+20 pts</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-2xl">✍️</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Publicar</p>
                    <p className="text-xs text-gray-500">+15 pts</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-2xl">💬</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Comentar</p>
                    <p className="text-xs text-gray-500">+5 pts</p>
                  </div>
                </div>
              </div>
            </Card>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Recompensas Disponibles</h3>
              <div className="grid gap-4">
                {rewards.map((reward) => {
                  const redeemed = hasRedeemed(reward.id);
                  const affordable = canAfford(reward.points_cost);

                  return (
                    <Card key={reward.id} className={`p-5 ${redeemed ? 'bg-gray-50 border-gray-200' : 'hover:shadow-md'} transition-shadow`}>
                      <div className="flex items-start gap-4">
                        <div className="text-4xl">{reward.icon}</div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900">{reward.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{reward.description}</p>
                          <div className="flex items-center gap-2 mt-3">
                            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                            <span className="font-bold text-amber-900">{reward.points_cost} puntos</span>
                          </div>
                        </div>
                        {redeemed ? (
                          <div className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Canjeado
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!affordable || redeemRewardMutation.isPending}
                            onClick={() => redeemRewardMutation.mutate(reward)}
                            className={affordable ? 'bg-amber-600 hover:bg-amber-700' : ''}
                          >
                            Canjear
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="milestones" className="space-y-6">
            <Card className="p-6 bg-gradient-to-br from-purple-500 to-pink-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Hitos Completados</p>
                  <p className="text-3xl font-bold">{achievedMilestones.length} / {MILESTONES.length}</p>
                </div>
                <Trophy className="w-12 h-12 opacity-30" />
              </div>
            </Card>

            {availableMilestones.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Hitos Disponibles</h3>
                <div className="space-y-3">
                  {availableMilestones.map((milestone) => (
                    <Card key={milestone.id} className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{milestone.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{milestone.name}</p>
                          <p className="text-sm text-emerald-600 font-medium mt-1">
                            +{milestone.reward} puntos de recompensa
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Meta</p>
                          <p className="font-bold text-gray-900">{milestone.target}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {achievedMilestones.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Hitos Alcanzados</h3>
                <div className="space-y-3">
                  {achievedMilestones.map((milestone) => (
                    <Card key={milestone.id} className="p-4 bg-green-50 border-green-200">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{milestone.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{milestone.name}</p>
                          <p className="text-sm text-green-600 font-medium">
                            ✓ Completado
                          </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Total de Transacciones</p>
                  <p className="text-3xl font-bold">{transactions.length}</p>
                </div>
                <History className="w-12 h-12 opacity-30" />
              </div>
            </Card>

            {transactions.length === 0 ? (
              <Card className="p-8 text-center">
                <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay transacciones aún</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <Card key={transaction.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl flex-shrink-0">
                        {getActionIcon(transaction.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900">{transaction.description}</p>
                          <div className="flex items-center gap-1">
                            {transaction.points > 0 ? (
                              <TrendingUp className="w-4 h-4 text-green-600" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-600" />
                            )}
                            <span className={`font-bold ${transaction.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {transaction.points > 0 ? '+' : ''}{transaction.points}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                            {getActionLabel(transaction.action)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(transaction.created_date), "d MMM yyyy, HH:mm", { locale: es })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}